import { Avatar } from "@/domains/shared/components/base/avatar/avatar";
import { CheckboxBase } from "@/domains/shared/components/base/checkbox/checkbox";
import { SelectItem as ShadcnSelectItem } from "@/components/ui/select";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";
import { Check } from "lucide-react";
import type { HTMLAttributes, ReactNode } from "react";
import { isValidElement, useContext } from "react";
import type { SelectItemType } from "./select-shared";
import { SelectContext } from "./select-shared";

const sizes = {
    sm: {
        root: "p-2 pr-2.5 gap-2 *:data-icon:size-4 *:data-icon:stroke-[2.25px]",
        text: "text-sm",
        textContainer: "gap-x-1.5",
        check: "size-4 stroke-[2.25px]",
        checkbox: "sm" as const,
    },
    md: {
        root: "p-2 pr-2.5 gap-2 *:data-icon:size-5",
        text: "text-md",
        textContainer: "gap-x-2",
        check: "size-5",
        checkbox: "sm" as const,
    },
    lg: {
        root: "p-2.5 pl-2 gap-2 *:data-icon:size-5",
        text: "text-md",
        textContainer: "gap-x-2",
        check: "size-5",
        checkbox: "md" as const,
    },
};

interface SelectItemProps extends SelectItemType, Omit<HTMLAttributes<HTMLElement>, "children" | "id"> {
    children?: ReactNode;
    textValue?: string;
    value?: SelectItemType;
    /** The selection indicator to be displayed on the item. */
    selectionIndicator?: "checkmark" | "checkbox" | "none";
    /** The alignment of the selection indicator. */
    selectionIndicatorAlign?: "left" | "right";
}

export const SelectItem = ({
    label,
    id,
    avatarUrl,
    supportingText,
    isDisabled,
    icon: Icon,
    className,
    children,
    textValue: _textValue,
    value: _value,
    selectionIndicator = "checkmark",
    selectionIndicatorAlign = "right",
    ...props
}: SelectItemProps) => {
    void _textValue;
    void _value;
    const { size, mode = "single", selectedKeys, onItemSelect } = useContext(SelectContext);

    const isLeft = selectionIndicatorAlign === "left";
    const isSelected = selectedKeys?.has(id) ?? false;

    const content = (
        <>
            {isLeft && selectionIndicator === "checkbox" ? (
                <CheckboxBase size={sizes[size].checkbox} isSelected={isSelected} disabled={isDisabled} />
            ) : null}

            {avatarUrl ? (
                <Avatar aria-hidden="true" size="xs" src={avatarUrl} alt={label} className={cx(size === "sm" && "size-5")} />
            ) : isReactComponent(Icon) ? (
                <Icon data-icon aria-hidden="true" />
            ) : isValidElement(Icon) ? (
                Icon
            ) : null}

            <div className={cx("flex w-full min-w-0 flex-1 flex-wrap", sizes[size].textContainer)}>
                <span className={cx("truncate font-medium whitespace-nowrap text-primary", sizes[size].text)}>
                    {label || children}
                </span>
                {supportingText ? (
                    <span className={cx("whitespace-nowrap text-muted-foreground", sizes[size].text)}>
                        {supportingText}
                    </span>
                ) : null}
            </div>

            {isSelected && selectionIndicator === "checkmark" ? (
                <Check aria-hidden="true" className={cx("ml-auto text-primary", sizes[size].check)} />
            ) : null}

            {!isLeft && selectionIndicator === "checkbox" ? (
                <CheckboxBase
                    size={sizes[size].checkbox}
                    isSelected={isSelected}
                    disabled={isDisabled}
                    className="ml-auto"
                />
            ) : null}
        </>
    );

    if (mode === "multiple") {
        return (
            <button
                type="button"
                role="option"
                aria-selected={isSelected}
                disabled={isDisabled}
                onClick={() => onItemSelect?.(id)}
                className={cx(
                    "flex w-full cursor-pointer items-center rounded-md outline-hidden select-none",
                    isSelected && selectionIndicator !== "checkbox" && "bg-accent",
                    "focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-inset",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                    "*:data-icon:shrink-0 *:data-icon:text-muted-foreground",
                    sizes[size].root,
                    className,
                )}
                {...props}
            >
                {content}
            </button>
        );
    }

    return (
        <ShadcnSelectItem
            value={id}
            disabled={isDisabled}
            {...props}
            className={cx(
                "w-full py-px",
                size === "sm" ? "px-1" : "px-1.5",
                className,
            )}
        >
            <div
                className={cx(
                    "flex w-full cursor-pointer items-center rounded-md outline-hidden select-none",
                    "*:data-icon:shrink-0 *:data-icon:text-muted-foreground",
                    sizes[size].root,
                )}
            >
                {content}
            </div>
        </ShadcnSelectItem>
    );
};
